import { ChartGranularity } from "../../../models/bidAnalytics";
import { OrderFailReason, OrderFailReasonDescription, OrderStatus } from "../../../models/order";
import {
    FailCulpritRow,
    FailReasonGameCell,
    FailReasonPoint,
    FailReasonRow,
    OrderFailCulprit,
    OrderFailCulpritDescription,
} from "../../../models/orderAnalytics";
import { toNum } from "../../bids/analytics/format";
import { ordersListUrl } from "./drilldown";
import { parseUtc } from "./summary";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const NO_REASON = "Без причины";
const OTHER = "Прочие";
const SERIES_COLORS = ["#c62828", "#ef6c00", "#6a1b9a", "#1565c0", "#00838f"];
const OTHER_COLOR = "#90a4ae";

const CULPRIT_COLORS: Record<OrderFailCulprit, string> = {
    [OrderFailCulprit.Seller]: "#c62828",
    [OrderFailCulprit.Buyer]: "#ef6c00",
    [OrderFailCulprit.Platform]: "#6a1b9a",
    [OrderFailCulprit.Supplier]: "#1565c0",
    [OrderFailCulprit.Admin]: "#2e7d32",
    [OrderFailCulprit.Other]: "#90a4ae",
    [OrderFailCulprit.P2PUndetermined]: "#8d6e63",
};

export function failReasonLabel(reason: OrderFailReason | null | undefined): string {
    if (reason === null || reason === undefined) {
        return NO_REASON;
    }
    return (OrderFailReasonDescription as Record<number, string>)[reason] ?? `Причина ${reason}`;
}

export function culpritLabel(culprit: OrderFailCulprit): string {
    return OrderFailCulpritDescription[culprit] ?? `Виновник ${culprit}`;
}

export type ReasonSortKey = "reason" | "count" | "sum" | "percent";

/** Новая отсортированная копия строк причин */
export function sortReasonRows(rows: FailReasonRow[], key: ReasonSortKey, desc: boolean): FailReasonRow[] {
    const value = (row: FailReasonRow): number | string => {
        switch (key) {
            case "reason":
                return failReasonLabel(row.failReason);
            case "sum":
                return toNum(row.sum);
            case "percent":
                return toNum(row.percentOfFailed);
            default:
                return toNum(row.count);
        }
    };
    const direction = desc ? -1 : 1;
    return [...rows].sort((a, b) => {
        const va = value(a);
        const vb = value(b);
        const cmp = typeof va === "string" ? va.localeCompare(vb as string, "ru") : va - (vb as number);
        return cmp * direction;
    });
}

/** Причины по количеству в порядке бэка (по убыванию) — для горизонтальных столбцов с полными подписями */
export function reasonBars(rows: FailReasonRow[]): { labels: string[]; counts: number[] } {
    return {
        labels: rows.map((r) => failReasonLabel(r.failReason)),
        counts: rows.map((r) => toNum(r.count)),
    };
}

export type CulpritItem = { id: OrderFailCulprit; value: number; label: string; color: string; percent: number };

/** Виновники для списка с полосками доли */
export function culpritItems(byCulprit: FailCulpritRow[]): CulpritItem[] {
    return byCulprit.map((c) => ({
        id: c.culprit,
        value: toNum(c.count),
        label: culpritLabel(c.culprit),
        color: CULPRIT_COLORS[c.culprit] ?? OTHER_COLOR,
        percent: toNum(c.percent),
    }));
}

export type ReasonGameMatrix = {
    games: Array<{ gameId: number; gameName: string }>;
    rows: Array<{ failReason: OrderFailReason | null; counts: number[] }>;
    max: number;
};

/** Таблица причина × игра: строки в порядке причин, игры по убыванию числа неуспешных, пустые ячейки — 0 */
export function reasonGameMatrix(cells: FailReasonGameCell[], reasons: FailReasonRow[]): ReasonGameMatrix {
    const totals = new Map<number, { gameName: string; total: number }>();
    for (const cell of cells) {
        const game = totals.get(cell.gameId) ?? { gameName: cell.gameName, total: 0 };
        totals.set(cell.gameId, { ...game, total: game.total + toNum(cell.count) });
    }
    const games = Array.from(totals.entries())
        .sort((a, b) => b[1].total - a[1].total || a[0] - b[0])
        .map(([gameId, g]) => ({ gameId, gameName: g.gameName }));

    const countOf = (reason: OrderFailReason | null, gameId: number) =>
        toNum(cells.find((c) => (c.failReason ?? null) === reason && c.gameId === gameId)?.count);
    const rows = reasons.map((r) => {
        const failReason = r.failReason ?? null;
        return { failReason, counts: games.map((g) => countOf(failReason, g.gameId)) };
    });

    return { games, rows, max: Math.max(0, ...rows.flatMap((r) => r.counts)) };
}

export type TopReasonsPoint = { x: Date; other: number } & Record<string, number | Date>;

/** Защита от зацикливания при заполнении пропусков на очень длинном периоде с мелким шагом */
const MAX_FILLED_POINTS = 10000;

function nextBucket(date: Date, granularity: ChartGranularity): Date {
    const next = new Date(date.getTime());
    if (granularity === "month") {
        next.setUTCMonth(next.getUTCMonth() + 1);
    } else {
        next.setTime(next.getTime() + (granularity === "hour" ? HOUR_MS : DAY_MS));
    }
    return next;
}

/** Бэк не отдаёт пустые интервалы — между первой и последней точкой добиваем их нулями, чтобы линия не сглаживала провал */
function fillGaps(points: TopReasonsPoint[], granularity: ChartGranularity,
                  empty: (x: Date) => TopReasonsPoint): TopReasonsPoint[] {
    if (points.length < 2) {
        return points;
    }
    const byTime = new Map(points.map((p) => [p.x.getTime(), p] as [number, TopReasonsPoint]));
    const last = points[points.length - 1].x.getTime();
    const result: TopReasonsPoint[] = [];
    for (let x = points[0].x; x.getTime() <= last && result.length < MAX_FILLED_POINTS; x = nextBucket(x, granularity)) {
        result.push(byTime.get(x.getTime()) ?? empty(x));
    }
    return result;
}

/** Линии топ-причин (r0…rN) и «прочие» по времени, по возрастанию; пропущенные интервалы — нули */
export function topReasonsDataset(series: FailReasonPoint[], topReasons: Array<OrderFailReason | null>,
                                  granularity: ChartGranularity): {
    dataset: TopReasonsPoint[];
    series: Array<{ dataKey: string; label: string; color: string }>;
} {
    const point = (x: Date, other: number, count: (reason: OrderFailReason | null) => number): TopReasonsPoint => {
        const row: TopReasonsPoint = { x, other };
        topReasons.forEach((reason, i) => {
            row[`r${i}`] = count(reason);
        });
        return row;
    };
    const points = series
        .map((p) => point(parseUtc(p.bucketUtc), toNum(p.otherCount),
            (reason) => toNum(p.reasons.find((r) => (r.failReason ?? null) === reason)?.count)))
        .sort((a, b) => a.x.getTime() - b.x.getTime());
    const dataset = fillGaps(points, granularity, (x) => point(x, 0, () => 0));

    return {
        dataset,
        series: [
            ...topReasons.map((reason, i) => ({
                dataKey: `r${i}`,
                label: failReasonLabel(reason),
                color: SERIES_COLORS[i % SERIES_COLORS.length],
            })),
            { dataKey: "other", label: OTHER, color: OTHER_COLOR },
        ],
    };
}

/** Статус списка ордеров для причины неуспеха: Refunded — Refunded, остальные (и без причины) — Failed */
export function failedStatusFor(reason: OrderFailReason | null | undefined): OrderStatus {
    return reason === OrderFailReason.Refunded ? OrderStatus.Refunded : OrderStatus.Failed;
}

/**
 * Ссылка в список ордеров с причиной, игрой и периодом фильтра; статус — {@link failedStatusFor}.
 * Без причины ссылки нет: в списке нельзя выбрать «пустую» причину.
 */
export function failReasonListUrl(
    reason: OrderFailReason | null | undefined,
    ctx: { gameId?: number; from: Date; to: Date }
): string | null {
    if (reason === null || reason === undefined) {
        return null;
    }
    return ordersListUrl({
        status: failedStatusFor(reason),
        failReason: reason,
        gameId: ctx.gameId,
        from: ctx.from,
        to: ctx.to,
    });
}
